/** Wall-clock time without a zone, as produced by `<input type="datetime-local">`. */
export const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** Returns the canonical IANA name (e.g. `america/chicago` → `America/Chicago`), or null. */
export function canonicalTimeZone(timeZone: string): string | null {
    if (!timeZone) return null;
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone;
    } catch {
        return null;
    }
}

/** Milliseconds since the epoch of a wall time read as if it were UTC, or null if malformed. */
function wallClockMs(value: string): number | null {
    const match = LOCAL_DATE_TIME.exec(value);
    if (!match) return null;
    const [year, month, day, hour, minute] = match.slice(1).map(Number) as [
        number,
        number,
        number,
        number,
        number,
    ];
    const ms = Date.UTC(year, month - 1, day, hour, minute);
    const date = new Date(ms);
    const roundTrips =
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day &&
        date.getUTCHours() === hour &&
        date.getUTCMinutes() === minute;
    return roundTrips ? ms : null;
}

/** True for calendar-valid wall times; rejects things like Feb 30 or 24:00. */
export function isValidLocalDateTime(value: string): boolean {
    return wallClockMs(value) !== null;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
    let formatter = formatters.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
        formatters.set(timeZone, formatter);
    }
    return formatter;
}

/** The wall time `instantMs` shows in `timeZone`, read as if it were UTC. */
function wallClockAt(instantMs: number, timeZone: string): number {
    const parts: Record<string, number> = {};
    for (const part of formatterFor(timeZone).formatToParts(instantMs)) {
        if (part.type !== 'literal') parts[part.type] = Number(part.value);
    }
    return Date.UTC(parts.year!, parts.month! - 1, parts.day!, parts.hour!, parts.minute!);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Converts a wall time in `timeZone` to an instant, with the same rules as
 * Temporal's "compatible" disambiguation: a time skipped by spring-forward
 * moves later by the size of the gap (2:30 → 3:30), and a time repeated at
 * fall-back resolves to its first occurrence.
 */
export function localToUtc(local: string, timeZone: string): Date {
    const wall = wallClockMs(local);
    if (wall === null) throw new RangeError(`Invalid local date-time: ${local}`);

    // Zone offsets a day either side of the wall time bracket any transition.
    const offsetBefore = wallClockAt(wall - DAY_MS, timeZone) - (wall - DAY_MS);
    const offsetAfter = wallClockAt(wall + DAY_MS, timeZone) - (wall + DAY_MS);
    const candidates = [wall - offsetBefore, wall - offsetAfter]
        .filter((instant) => wallClockAt(instant, timeZone) === wall)
        .sort((a, b) => a - b);

    return new Date(candidates[0] ?? wall - offsetBefore);
}

export function utcToLocal(instant: Date, timeZone: string): string {
    return new Date(wallClockAt(instant.getTime(), timeZone)).toISOString().slice(0, 16);
}
